import { Component, forwardRef } from 'react';
import { handleRefSplit } from '@optimus-web/common';
import { withClickAway, withScrollAway } from '@optimus-web/helpers';
import { IconButton, IconButtonProps } from '../IconButton';
import Menu from './Menu';
import { IconMenuProps } from './MenuProps';
import { IconMenuElement } from './style';

interface IconMenuState {
    opened: boolean;
}

class IconMenu extends Component<IconMenuProps, IconMenuState> {
    private containerElement?: HTMLElement;
    private menuElement?: HTMLElement | null;
    private icon?: HTMLElement;

    static defaultProps = {
        buttonVariant: 'ghost',
    };

    state = {
        opened: false,
    };

    handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (!this.state.opened) {
            const options = {
                handler: this.close,
                roots: [() => this.containerElement, () => this.menuElement],
            };
            this.props.registerClickAway(options);
            this.props.registerScrollAway(options);
            this.setState({ opened: true });
        }
    };

    handleItemClick = () => {
        this.close();
    };

    close = () => {
        if (this.state.opened) {
            this.props.deregisterClickAway();
            this.props.deregisterScrollAway();
            this.setState({ opened: false });
        }
    };

    handleContainerRef = handleRefSplit<HTMLElement | null>({
        ownRef: (c: HTMLElement) => (this.containerElement = c),
        forwardedRef: this.props.forwardedRef,
    });
    handleMenuRef = (component: HTMLElement | null) => (this.menuElement = component);
    handleIconRef = (component: HTMLElement) => (this.icon = component);

    render() {
        const { icon, children, className, buttonVariant, buttonSize, overlayMinWidth, ...other } = this.props;
        const { opened } = this.state;

        const iconMenuProps = {
            className,
            ref: this.handleContainerRef,
        };

        const buttonProps: IconButtonProps = {
            icon,
            onClick: this.handleClick,
            ref: (component: HTMLButtonElement) => (this.icon = component),
            variant: buttonVariant,
            size: buttonSize,
            ...other,
        };

        const menuProps = {
            ...other,
            anchorElement: () => this.icon,
            onItemClick: this.handleItemClick,
            ref: this.handleMenuRef,
            opened,
            overlayMinWidth,
        };

        return (
            <IconMenuElement {...iconMenuProps}>
                <IconButton {...buttonProps} />
                <Menu {...menuProps}>{children}</Menu>
            </IconMenuElement>
        );
    }
}

export default withScrollAway(
    withClickAway(forwardRef<HTMLDivElement, IconMenuProps>((props, ref) => <IconMenu {...props} forwardedRef={ref} />))
);
